import React, { type ReactElement, type ReactNode } from 'react';
import classnames from 'classnames';

import { CHART_DATA_THEMES } from './constant';
import SemiCircularChart, { type PlotPoint } from './SemiCircularChart';

import './RiskGraph.css';

export interface ChartScales {
  x_min: number;
  x_max: number;
  y_min: number;
  y_max: number;
}

export type ChartDataTheme =
  (typeof CHART_DATA_THEMES)[keyof typeof CHART_DATA_THEMES];

interface RiskGraphProps {
  plotPoints: PlotPoint[];
  centerCount: number;
  chartScales: ChartScales;
  isAnimation: boolean;
  isProcessing: boolean;
  isFilterActive: boolean;
  filterCategories: string[];
  filterTooltips: string[];
  onPointClick: (point: PlotPoint, isAlreadyActive?: boolean) => void;
  onSectorClick: (sector: unknown) => void;
  handleFilterClick?: (value: string) => void;
  secondaryContent?: ReactNode;
  isPointHoverDisabled?: boolean;
  isSectorInteractive: boolean;
  xAxisTitle: string;
  yAxisTitle: string;
  dataTheme?: ChartDataTheme;
}

/**
 * RiskGraph
 */
function RiskGraph({
  plotPoints,
  centerCount,
  chartScales,
  isAnimation,
  isFilterActive,
  isProcessing,
  filterCategories,
  filterTooltips,
  onPointClick,
  onSectorClick,
  handleFilterClick,
  secondaryContent,
  isPointHoverDisabled,
  isSectorInteractive,
  xAxisTitle,
  yAxisTitle,
  dataTheme = CHART_DATA_THEMES.DARK,
}: RiskGraphProps): ReactElement {
  const riskGraphClasses = classnames(
    'RiskGraph',
    dataTheme === CHART_DATA_THEMES.DARK && 'RiskGraph--isDark'
  );

  return (
    <div className={riskGraphClasses}>
      <div className='RiskGraph-wrapper'>
        <div className='RiskGraph-chartArea'>
          <SemiCircularChart
            plotPoints={plotPoints}
            centerCount={centerCount}
            chartScales={chartScales}
            isAnimation={isAnimation}
            isProcessing={isProcessing}
            isFilterActive={isFilterActive}
            filterCategories={filterCategories}
            filterPlacement={[]}
            filterTooltips={filterTooltips}
            isSectorInteractive={isSectorInteractive}
            onPointClick={onPointClick}
            onSectorClick={onSectorClick}
            handleFilterClick={handleFilterClick}
            isPointHoverDisabled={isPointHoverDisabled}
            secondaryContent={secondaryContent}
            xAxisTitle={xAxisTitle}
            yAxisTitle={yAxisTitle}
            xAxisLabelSpacing='tri'
            yAxisLabelSpacing='bi'
          />
        </div>
      </div>
    </div>
  );
}

RiskGraph.defaultProps = {
  secondaryContent: null,
  isPointHoverDisabled: false,
  handleFilterClick: () => {},
  dataTheme: CHART_DATA_THEMES.DARK,
};

export default RiskGraph;
